import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { toast } from "react-hot-toast";

import { client } from "../../utils/supabase";
import styles from "./PulsoRegional.module.scss";

const TABLE = "pulso_regional_seen";

/** Normaliza la key de empresa para garantizar consistencia */
const buildKey = (empresa, paisOrigen) => `${(empresa || "").toLowerCase().trim()}|${paisOrigen}`;

const getEstatusClass = (maxEstatus) => {
  if (maxEstatus >= 100) return styles.estatus100;
  if (maxEstatus >= 90) return styles.estatus90;
  if (maxEstatus >= 75) return styles.estatus75;
  return "";
};

const EXTERNAL_ROLES = new Set(["agencia", "cliente", "edificio", "inversor"]);

const PulsoRegional = ({ alerts = [], loading = false, rol = "", dolarOtroPais = 0, usuarioId = "" }) => {
  const canMark = !!rol && !EXTERNAL_ROLES.has(rol);
  const [seen, setSeen] = useState({});
  const [isOpen, setIsOpen] = useState(true);
  const savingRef = useRef(false);

  // Cargar "seen" desde Supabase al montar
  useEffect(() => {
    if (!usuarioId) return;
    const fetchSeen = async () => {
      const { data, error } = await client.from(TABLE).select("empresa_key, seen_at").eq("usuario_id", usuarioId);
      if (error) {
        console.error("Error cargando pulso seen:", error); // eslint-disable-line no-console
        return;
      }
      const map = {};
      for (const row of data || []) {
        map[row.empresa_key] = row.seen_at;
      }
      setSeen(map);
    };
    fetchSeen();
  }, [usuarioId]);

  const isAlertSeen = useCallback(
    (alert) => {
      const key = buildKey(alert.empresa, alert.paisOrigen);
      const seenAt = seen[key];
      if (!seenAt) return false;
      return new Date(seenAt) >= new Date(alert.fecha);
    },
    [seen]
  );

  const unseenCount = useMemo(() => alerts.filter((a) => !isAlertSeen(a)).length, [alerts, isAlertSeen]);

  const handleToggleSeen = useCallback(
    async (alert) => {
      if (!usuarioId || savingRef.current) return;
      savingRef.current = true;

      const key = buildKey(alert.empresa, alert.paisOrigen);
      const alreadySeen = isAlertSeen(alert);

      try {
        if (alreadySeen) {
          const { error } = await client.from(TABLE).delete().eq("usuario_id", usuarioId).eq("empresa_key", key);
          if (error) throw error;
          const updated = { ...seen };
          delete updated[key];
          setSeen(updated);
        } else {
          const now = new Date().toISOString();
          const payload = { usuario_id: usuarioId, empresa_key: key, seen_at: now };
          const { error } = await client.from(TABLE).upsert(payload, { onConflict: "usuario_id,empresa_key" });
          if (error) throw error;
          setSeen({ ...seen, [key]: now });
        }
      } catch (err) {
        console.error("Error actualizando pulso seen:", err); // eslint-disable-line no-console
        toast.error("Error al marcar alerta");
      } finally {
        savingRef.current = false;
      }
    },
    [seen, isAlertSeen, usuarioId]
  );

  if (loading) return null;
  if (alerts.length === 0) return null;

  const formatFecha = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("es-CL", { day: "2-digit", month: "short" });
  };

  const formatMonto = (amount) => {
    const num = Number(amount) || 0;
    if (num === 0) return "-";
    return num.toLocaleString("es-CL", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header} onClick={() => setIsOpen(!isOpen)}>
        <span className={styles.headerTitle}>Pulso Comercial Regional</span>
        {unseenCount > 0 && <span className={styles.badge}>{unseenCount}</span>}
        <span className={`${styles.chevron} ${isOpen ? styles.open : ""}`}>&#9660;</span>
      </div>

      {isOpen && (
        <div className={styles.content}>
          {alerts.map((alert, idx) => {
            const paisClass = alert.paisOrigen === "Chile" ? styles.chile : styles.peru;
            const estatusClass = getEstatusClass(alert.maxEstatus);
            const alertSeen = isAlertSeen(alert);
            let totalUSD = alert.montoUSD || 0;
            if (alert.montoLocal && dolarOtroPais > 0) {
              totalUSD += Math.round(alert.montoLocal / dolarOtroPais);
            }
            const montoDisplay = totalUSD > 0 ? `USD ${formatMonto(totalUSD)}` : "-";

            return (
              <div
                key={`${alert.empresa}-${idx}`}
                className={[
                  styles.card,
                  estatusClass,
                  alertSeen ? styles.seen : "",
                  canMark ? styles.clickable : "",
                ].join(" ")}
                onClick={canMark ? () => handleToggleSeen(alert) : undefined}
                title={canMark ? (alertSeen ? "Click para desmarcar" : "Click para marcar como vista") : undefined}
              >
                {alertSeen && <span className={styles.seenCheck}>&#10003;</span>}
                <div className={styles.cardHeader}>
                  <span className={styles.empresa} title={alert.empresa}>
                    {alert.empresa}
                  </span>
                  <span className={`${styles.paisBadge} ${paisClass}`}>{alert.paisOrigen}</span>
                </div>
                <div className={styles.cardBody}>
                  <div className={styles.cardRow}>
                    <span className={styles.cardLabel}>Fecha</span>
                    <span className={styles.cardValue}>{formatFecha(alert.fecha)}</span>
                  </div>
                  <div className={styles.cardRow}>
                    <span className={styles.cardLabel}>Monto</span>
                    <span className={styles.cardValue}>{montoDisplay}</span>
                  </div>
                  {alert.maxEstatus > 0 && (
                    <div className={styles.cardRow}>
                      <span className={styles.cardLabel}>Estatus</span>
                      <span className={styles.cardValue}>{alert.maxEstatus}%</span>
                    </div>
                  )}
                  {alert.plazo > 0 && (
                    <div className={styles.cardRow}>
                      <span className={styles.cardLabel}>Plazo</span>
                      <span className={styles.cardValue}>{alert.plazo} días</span>
                    </div>
                  )}
                  {alert.categoria && (
                    <div className={styles.cardRow}>
                      <span className={styles.cardLabel}>Categoría</span>
                      <span className={styles.cardValue} title={alert.categoria}>
                        {alert.categoria.length > 18 ? alert.categoria.slice(0, 18) + "…" : alert.categoria}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PulsoRegional;
